import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import Modal from '../../components/ui/Modal';
import FormInput from '../../components/forms/FormInput';
import { getStaff, getTodayRoster, getRosterByDate, filterStaff, createStaff, updateStaff, deleteStaff, getAttendance, saveAttendance, uploadStaffPhoto, uploadStaffAadhar } from '../../api/staff';


const tabs = [
  { key: 'all', label: 'Scheduled Workers Details' },
  { key: 'staff', label: 'Staff Directory' },
  { key: 'attendance', label: 'Attendance Tracker' },
  { key: 'leave', label: 'Leave Management' },
  { key: 'reports', label: 'Reports' },
];

const towerAFloors = ['1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18'];
const towerBFloors = Array.from({ length: 18 }, (_, i) => String(i + 1));
const securityPostsList = ['Gate 1', 'Gate 2', 'Main Entrance', 'Common Area'];
const towerCleaningSections = ['Floor Mopping', 'Floor Sweeping', 'Garbage Removal'];
const parkingCleaningSections = ['Parking B1', 'Parking B2', 'Parking 1'];
const otherCleaningSections = ['Lobby GF', 'Watering Plants', 'Conference Hall', 'Ladies Gym', 'Multi Gym', 'Common Toilets', 'Front Area', 'Back Side', 'Tracks', 'Games Room'];
const allCleaningSections = [...towerCleaningSections, ...parkingCleaningSections, ...otherCleaningSections];

const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dayLabels = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const shiftOptions = [
  { value: '', label: 'All Shifts' },
  { value: 'day', label: 'Day Shift' },
  { value: 'evening', label: 'Evening Shift' },
  { value: 'night', label: 'Night Shift' },
];

const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const getAssignmentSummary = (member) => {
  if (!member.assignments || member.assignments.length === 0) return null;
  if (member.staff_type === 'security') {
    return { posts: member.assignments.map((a) => a.security_post).filter(Boolean) };
  }
  const towers = {};
  const sections = [];
  member.assignments.forEach((a) => {
    if (a.assignment_type === 'tower_floor' && a.tower) {
      if (!towers[a.tower]) towers[a.tower] = [];
      if (a.floor) towers[a.tower].push(a.floor);
    }
    if (a.assignment_type === 'cleaning_section' && a.cleaning_section) {
      sections.push(a.cleaning_section);
    }
  });
  return { towers, sections };
};

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split('T')[0];

  // Add Staff modal state
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationStatus, setAllocationStatus] = useState('present');
  const [allocationDate, setAllocationDate] = useState(today);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [addStaffSubmitting, setAddStaffSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [addStaffForm, setAddStaffForm] = useState({
    name: '', phone: '', staff_type: 'cleaning',
    work_start_time: '08:30', work_end_time: '16:15',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
  });

  // Staff Directory state
  const [allStaff, setAllStaff] = useState([]);
  const [allStaffLoading, setAllStaffLoading] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryType, setDirectoryType] = useState('');
  const [expandedStaffId, setExpandedStaffId] = useState(null);

  // Attendance state
  const [attendance, setAttendance] = useState({});
  const [attendanceDate, setAttendanceDate] = useState(today);
  const [attendanceTypeFilter, setAttendanceTypeFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('');

  // Leave Management state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    staffId: '',
    leaveType: 'Casual',
    fromDate: today,
    toDate: today,
    reason: '',
  });

  const [filters, setFilters] = useState({
    service_type: '',
    name: '',
    area: '',
    floor: '',
    cleaning_section: '',
    security_post: '',
    shift: '',
    date: today,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const hasFilters = filters.service_type || filters.area || filters.floor || filters.shift || filters.security_post;

      let staffRes;
      if (hasFilters) {
        const filterPayload = {};
        if (filters.service_type) filterPayload.staff_type = filters.service_type;
        if (filters.area) filterPayload.tower = filters.area;
        if (filters.floor) filterPayload.floor = filters.floor;
        if (filters.shift) filterPayload.shift_type = filters.shift;
        staffRes = await filterStaff(filterPayload);
      } else if (filters.date && filters.date !== today) {
        staffRes = await getRosterByDate(filters.date);
      } else {
        staffRes = await getTodayRoster();
      }

      let data = staffRes.data?.data || staffRes.data || [];
      data = Array.isArray(data) ? data : [];
      if (filters.name) {
        data = data.filter((s) => s.name === filters.name);
      }
      if (filters.cleaning_section) {
        data = data.filter((s) => s.assignments?.some((a) => a.cleaning_section === filters.cleaning_section));
      }
      if (filters.security_post) {
        data = data.filter((s) => s.assignments?.some((a) => a.security_post === filters.security_post));
      }
      setStaff(data);
    } catch (err) {
      console.error('Failed to load staff roster', err);
    } finally {
      setLoading(false);
    }
  }, [filters, today]);


  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAllStaff = useCallback(() => {
    setAllStaffLoading(true);
    getStaff({ all: true })
      .then((res) => {
        const data = res.data || res || [];
        setAllStaff(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Failed to load all staff', err))
      .finally(() => setAllStaffLoading(false));
  }, []);

  // Fetch all staff for directory, attendance, leave, reports tabs
  useEffect(() => {
    if (activeTab !== 'all' && allStaff.length === 0 && !allStaffLoading) {
      fetchAllStaff();
    }
  }, [activeTab, allStaff.length, allStaffLoading, fetchAllStaff]);

  // Shared effective date for attendance (sync both tabs)
  const effectiveDate = activeTab === 'attendance' ? attendanceDate : (filters.date || today);

  // Load attendance from API when date changes or staff loads
  useEffect(() => {
    const combined = [...staff, ...allStaff];
    if (combined.length > 0) {
      getAttendance(effectiveDate)
        .then((res) => {
          const saved = res.data || {};
          const updated = {};
          combined.forEach((s) => {
            updated[s.id] = saved[s.id] || (s.is_active ? 'present' : 'absent');
          });
          setAttendance(updated);
        })
        .catch(() => {
          const updated = {};
          combined.forEach((s) => {
            updated[s.id] = s.is_active ? 'present' : 'absent';
          });
          setAttendance(updated);
        });
    }
  }, [allStaff, staff, effectiveDate]);

  // Filtered staff for directory
  const filteredDirectory = useMemo(() => {
    let result = allStaff;
    if (directorySearch) {
      const q = directorySearch.toLowerCase();
      result = result.filter((s) => s.name?.toLowerCase().includes(q));
    }
    if (directoryType) {
      result = result.filter((s) => s.staff_type === directoryType);
    }
    return result;
  }, [allStaff, directorySearch, directoryType]);

  // Attendance stats
  const attendanceValues = Object.values(attendance);
  const attendanceStats = {
    total: allStaff.length,
    present: attendanceValues.filter((v) => v === 'present').length,
    absent: attendanceValues.filter((v) => v === 'absent').length,
    leave: attendanceValues.filter((v) => v === 'leave').length,
  };

  // Leave stats
  const leaveStats = useMemo(() => ({
    total: leaveRequests.length,
    pending: leaveRequests.filter((r) => r.status === 'Pending').length,
    approved: leaveRequests.filter((r) => r.status === 'Approved').length,
    rejected: leaveRequests.filter((r) => r.status === 'Rejected').length,
  }), [leaveRequests]);

  // Reports data
  const reportData = useMemo(() => {
    const cleaningCount = allStaff.filter((s) => s.staff_type === 'cleaning').length;
    const secCount = allStaff.filter((s) => s.staff_type === 'security').length;

    const shifts = { day: 0, evening: 0, night: 0, unknown: 0 };
    allStaff.forEach((s) => {
      const startHour = s.work_start_time ? parseInt(s.work_start_time.split(':')[0], 10) : -1;
      if (startHour >= 5 && startHour < 14) {
        shifts.day++;
      } else if (startHour >= 14 && startHour < 21) {
        shifts.evening++;
      } else if (startHour >= 21 || (startHour >= 0 && startHour < 5)) {
        shifts.night++;
      } else {
        shifts.unknown++;
      }
    });

    const towerAllocation = {};
    allStaff.forEach((s) => {
      if (s.assignments) {
        s.assignments.forEach((a) => {
          if (a.tower) {
            towerAllocation[a.tower] = (towerAllocation[a.tower] || 0) + 1;
          }
          if (a.security_post) {
            towerAllocation[a.security_post] = (towerAllocation[a.security_post] || 0) + 1;
          }
        });
      }
    });

    const dayCoverage = {};
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    dayNames.forEach((d) => { dayCoverage[d] = 0; });
    allStaff.forEach((s) => {
      if (s.working_days) {
        const days = typeof s.working_days === 'string' ? JSON.parse(s.working_days) : s.working_days;
        if (Array.isArray(days)) {
          days.forEach((d) => {
            const capitalized = d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
            if (dayCoverage[capitalized] !== undefined) {
              dayCoverage[capitalized]++;
            }
          });
        }
      }
    });

    return { cleaningCount, secCount, shifts, towerAllocation, dayCoverage };
  }, [allStaff]);

  // Add Staff form handlers
  const handleAddStaffChange = (e) => setAddStaffForm({ ...addStaffForm, [e.target.name]: e.target.value });

  const toggleAddStaffDay = (day) => {
    const days = addStaffForm.working_days.includes(day)
      ? addStaffForm.working_days.filter((d) => d !== day)
      : [...addStaffForm.working_days, day];
    setAddStaffForm({ ...addStaffForm, working_days: days });
  };

  const toggleAddStaffArrayItem = (field, item) => {
    const arr = addStaffForm[field];
    const updated = arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
    setAddStaffForm({ ...addStaffForm, [field]: updated });
  };

  const resetAddStaffForm = () => {
    setAddStaffForm({
      name: '', phone: '', staff_type: 'cleaning',
      work_start_time: '08:30', work_end_time: '16:15',
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [],
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setAadharFile(null);
  };

  const handleAddStaffSubmit = async (e) => {
    e.preventDefault();
    setAddStaffSubmitting(true);
    try {
      const assignments = [];
      if (addStaffForm.staff_type === 'cleaning') {
        if (addStaffForm.towers.includes('Tower A')) {
          addStaffForm.floorsA.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower A', floor, security_post: null, cleaning_section: null });
          });
        }
        if (addStaffForm.towers.includes('Tower B')) {
          addStaffForm.floorsB.forEach((floor) => {
            assignments.push({ assignment_type: 'tower_floor', tower: 'Tower B', floor, security_post: null, cleaning_section: null });
          });
        }
        addStaffForm.cleaningSections.forEach((section) => {
          assignments.push({ assignment_type: 'cleaning_section', tower: null, floor: null, security_post: null, cleaning_section: section });
        });
      } else {
        addStaffForm.securityPosts.forEach((post) => {
          assignments.push({ assignment_type: 'security_post', tower: null, floor: null, security_post: post });
        });
      }

      // Upload photo and aadhar if selected
      let photoPath = null;
      let aadharPath = null;
      if (photoFile) {
        const photoRes = await uploadStaffPhoto(photoFile);
        photoPath = photoRes.data.path;
      }
      if (aadharFile) {
        const aadharRes = await uploadStaffAadhar(aadharFile);
        aadharPath = aadharRes.data.path;
      }

      const isAbsent = showAllocationModal && allocationStatus === 'absent';
      const payload = {
        name: addStaffForm.name,
        phone: addStaffForm.phone || null,
        staff_type: addStaffForm.staff_type,
        shift_type: 'Day',
        work_start_time: addStaffForm.work_start_time || '08:30',
        work_end_time: addStaffForm.work_end_time || '16:15',
        working_days: addStaffForm.working_days,
        is_active: true,
        assignments: assignments.length > 0 ? assignments : [],
        ...(photoPath && { photo_path: photoPath }),
        ...(aadharPath && { aadhar_path: aadharPath }),
      };

      if (editingStaffId) {
        await updateStaff(editingStaffId, payload);
        // Save attendance status separately
        if (showAllocationModal) {
          const dateToSave = allocationDate || today;
          await saveAttendance({
            date: dateToSave,
            attendance: [{ staff_member_id: editingStaffId, status: isAbsent ? 'absent' : 'present' }],
          });
          setAttendance((prev) => ({ ...prev, [editingStaffId]: isAbsent ? 'absent' : 'present' }));
        }
      } else {
        await createStaff(payload);
      }
      setShowAddStaffModal(false);
      setShowAllocationModal(false);
      setAllocationStatus('present');
      setAllocationDate(today);
      setEditingStaffId(null);
      resetAddStaffForm();
      fetchData();
      fetchAllStaff();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create staff');
    } finally {
      setAddStaffSubmitting(false);
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    try {
      await deleteStaff(id);
      fetchData();
      fetchAllStaff();
    } catch (err) {
      alert('Failed to delete staff');
    }
  };

  const handleLeaveSubmit = () => {
    if (!leaveForm.staffId || !leaveForm.fromDate || !leaveForm.toDate) return;
    const staffMember = allStaff.find((s) => String(s.id) === String(leaveForm.staffId));
    const from = new Date(leaveForm.fromDate);
    const to = new Date(leaveForm.toDate);
    const days = Math.max(1, Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1);
    setLeaveRequests((prev) => [
      ...prev,
      {
        id: Date.now(),
        staffName: staffMember?.name || 'Unknown',
        staffId: leaveForm.staffId,
        leaveType: leaveForm.leaveType,
        fromDate: leaveForm.fromDate,
        toDate: leaveForm.toDate,
        days,
        reason: leaveForm.reason,
        status: 'Pending',
      },
    ]);
    setLeaveForm({ staffId: '', leaveType: 'Casual', fromDate: today, toDate: today, reason: '' });
    setShowLeaveModal(false);
  };

  const updateLeaveStatus = (id, status) => {
    setLeaveRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ service_type: '', name: '', area: '', floor: '', cleaning_section: '', security_post: '', shift: '', date: today });
  };

  const applyFilters = () => {
    fetchData();
  };


  const getFloorOptions = () => {
    switch (filters.area) {
      case 'Tower A': return towerAFloors;
      case 'Tower B': return towerBFloors;
      default: return [];
    }
  };

  const staffNames = [...new Set(staff.map((s) => s.name))].sort();
  const isCleaning = filters.service_type === 'cleaning';
  const isSecurity = filters.service_type === 'security';

  return (
    <AppLayout title="Work Management" breadcrumbs="Work Management">
      {/* Sub Navigation */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{
          background: 'white',
          borderBottom: '1px solid #E5E5E0',
          padding: '0 40px',
          gap: '32px',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#043959';
                e.currentTarget.style.borderBottomColor = '#F4A460';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#666666';
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '32px 0' }}>
        {activeTab === 'all' && (<>
        {/* Header with actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" style={{ marginBottom: '24px' }}>
          <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Work Management</h3>
          <div className="flex flex-wrap gap-2">
          </div>
        </div>

        {/* Filter Bar */}
        <div
          className="bg-white"
          style={{ borderRadius: '12px', border: '1px solid #E5E5E0', padding: '20px', marginBottom: '24px' }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            {/* Staff Type */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Staff Type</label>
              <select
                value={filters.service_type}
                onChange={(e) => {
                  setFilters({ service_type: e.target.value, name: '', area: '', floor: '', cleaning_section: '', security_post: '', shift: '', date: filters.date });
                }}
                className="w-full"
                style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
              >
                <option value="">All Types</option>
                <option value="cleaning">Cleaning Staff</option>
                <option value="security">Security Staff</option>
              </select>
            </div>

            {/* Staff Name */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Staff Name</label>
              <select
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                className="w-full"
                style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
              >
                <option value="">All Staff</option>
                {staffNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Cleaning: Area */}
            {(!filters.service_type || isCleaning) && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Area</label>
                <select
                  value={filters.area}
                  onChange={(e) => setFilters((prev) => ({ ...prev, area: e.target.value, floor: '' }))}
                  className="w-full"
                  style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Areas</option>
                  <option value="Tower A">Tower A</option>
                  <option value="Tower B">Tower B</option>
                  <option value="Parking">Parking</option>
                  <option value="Others">Others</option>
                </select>
              </div>
            )}

            {/* Cleaning: Floor (if Tower A or B selected) */}
            {(!filters.service_type || isCleaning) && (filters.area === 'Tower A' || filters.area === 'Tower B') && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Floor</label>
                <select
                  value={filters.floor}
                  onChange={(e) => handleFilterChange('floor', e.target.value)}
                  className="w-full"
                  style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Floors</option>
                  {getFloorOptions().map((f) => (
                    <option key={f} value={f}>Floor {f}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Cleaning: Cleaning Sections */}
            {(!filters.service_type || isCleaning) && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Cleaning Section</label>
                <select
                  value={filters.cleaning_section}
                  onChange={(e) => handleFilterChange('cleaning_section', e.target.value)}
                  className="w-full"
                  style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Sections</option>
                  {(filters.area === 'Tower A' || filters.area === 'Tower B'
                    ? towerCleaningSections
                    : filters.area === 'Parking'
                      ? parkingCleaningSections
                      : filters.area === 'Others'
                        ? otherCleaningSections
                        : allCleaningSections
                  ).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Security: Post Assignment */}
            {isSecurity && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Security Post</label>
                <select
                  value={filters.security_post}
                  onChange={(e) => handleFilterChange('security_post', e.target.value)}
                  className="w-full"
                  style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Posts</option>
                  {securityPostsList.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Security: Shift Type */}
            {isSecurity && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Shift Type</label>
                <select
                  value={filters.shift}
                  onChange={(e) => handleFilterChange('shift', e.target.value)}
                  className="w-full"
                  style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  {shiftOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Date</label>
              <input
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                className="w-full"
                style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
              />
            </div>

            {/* Clear & Apply */}
            <div className="flex gap-2 items-end">
              <button
                onClick={clearFilters}
                className="text-sm font-medium rounded-lg cursor-pointer"
                style={{ padding: '8px 16px', background: '#FAFAF8', color: '#666666', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                style={{ padding: '8px 16px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        {!loading && staff.length > 0 && (() => {
          const absentStaff = staff.filter((s) => {
            const status = attendance[s.id];
            return status === 'absent' || status === 'leave';
          });
          const unassignedPresent = staff.filter((s) => {
            const status = attendance[s.id] || 'present';
            return status === 'present' && (!s.assignments || s.assignments.length === 0);
          });
          if (absentStaff.length === 0 && unassignedPresent.length === 0) return null;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              {absentStaff.length > 0 && (
                <div
                  style={{
                    padding: '12px 16px', borderRadius: '10px',
                    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
                  }}
                >
                  <div className="flex items-center gap-2" style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '14px' }}>⚠</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400E' }}>
                      {absentStaff.length} staff {absentStaff.length === 1 ? 'is' : 'are'} absent/on leave — areas not allocated
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#92400E', paddingLeft: '22px' }}>
                    {absentStaff.map((s) => {
                      const summary = getAssignmentSummary(s);
                      let areas = [];
                      if (summary) {
                        if (summary.posts) areas.push(...summary.posts);
                        if (summary.towers) {
                          Object.entries(summary.towers).forEach(([tower, floors]) => {
                            areas.push(`${tower} (${floors.join(', ')})`);
                          });
                        }
                        if (summary.sections && summary.sections.length > 0) areas.push(...summary.sections);
                      }
                      return (
                        <div key={s.id} style={{ marginBottom: '2px' }}>
                          <strong>{s.name}</strong>{areas.length > 0 ? ` — ${areas.join(', ')}` : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {unassignedPresent.length > 0 && (
                <div
                  style={{
                    padding: '12px 16px', borderRadius: '10px',
                    background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  <div className="flex items-center gap-2" style={{ marginBottom: '6px' }}>
                    <span style={{ fontSize: '14px' }}>⚠</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#991B1B' }}>
                      {unassignedPresent.length} present staff {unassignedPresent.length === 1 ? 'has' : 'have'} no area allocated
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#991B1B', paddingLeft: '22px' }}>
                    {unassignedPresent.map((s) => (
                      <div key={s.id} style={{ marginBottom: '2px' }}>
                        <strong>{s.name}</strong> — No position/area assigned
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Staff Roster Table */}
        {loading ? (
          <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>
            Loading staff roster...
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>
            No staff found for the selected filters
          </div>
        ) : (
          <div
            className="bg-white overflow-hidden"
            style={{ borderRadius: '16px', border: '1px solid #E5E5E0', marginBottom: '24px' }}
          >
            <div className="overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                    {['Staff Name', 'Service Type', 'Position/Area', 'Shift', 'Date', 'Status', 'Contact', 'Actions'].map((h) => (
                      <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staff.map((member) => {
                    const memberAttStatus = attendance[member.id] || 'present';
                    const isWorking = memberAttStatus === 'present';
                    const summary = getAssignmentSummary(member);
                    let positionBadges = [];
                    if (isWorking && summary) {
                      if (summary.posts) {
                        positionBadges = [...summary.posts];
                      }
                      if (summary.towers) {
                        Object.entries(summary.towers).forEach(([tower, floors]) => {
                          positionBadges.push(`${tower} (${floors.join(', ')})`);
                        });
                      }
                      if (summary.sections && summary.sections.length > 0) {
                        positionBadges.push(...summary.sections);
                      }
                    }

                    return (
                      <tr key={member.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <div className="flex items-center gap-3">
                            <div
                              className="flex items-center justify-center text-white font-semibold"
                              style={{
                                width: '36px', height: '36px', borderRadius: '50%', fontSize: '12px',
                                background: 'linear-gradient(135deg, #043959, #F4A460)', flexShrink: 0,
                              }}
                            >
                              {getInitials(member.name)}
                            </div>
                            <strong style={{ fontSize: '14px', color: '#1A1A1A' }}>{member.name}</strong>
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              display: 'inline-block', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                              background: member.staff_type === 'security' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)',
                              color: member.staff_type === 'security' ? '#3B82F6' : '#10B981',
                            }}
                          >
                            {member.staff_type === 'security' ? 'Security' : 'Cleaning'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {positionBadges.length > 0 ? positionBadges.join(', ') : (
                            <span style={{ fontSize: '13px', color: '#999999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                          {isWorking ? `${formatTime12(member.work_start_time)} - ${formatTime12(member.work_end_time)}` : (
                            <span style={{ color: '#999999' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666666', whiteSpace: 'nowrap' }}>
                          {filters.date ? new Date(filters.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Today'}
                        </td>
                        <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                          {(() => {
                            const attStatus = attendance[member.id] || (member.is_active ? 'present' : 'absent');
                            const statusStyle = {
                              present: { bg: 'rgba(16,185,129,0.1)', color: '#10B981', label: 'Present' },
                              absent: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', label: 'Absent' },
                              leave: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B', label: 'On Leave' },
                            }[attStatus] || { bg: 'rgba(16,185,129,0.1)', color: '#10B981', label: 'Present' };
                            return (
                              <span
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                                  background: statusStyle.bg, color: statusStyle.color,
                                }}
                              >
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />
                                {statusStyle.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                          {member.phone || '-'}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                resetAddStaffForm();
                                setEditingStaffId(member.id);
                                const towers = [];
                                const floorsA = [];
                                const floorsB = [];
                                const secPosts = [];
                                const cleanSections = [];
                                (member.assignments || []).forEach((a) => {
                                  if (a.assignment_type === 'tower_floor' && a.tower) {
                                    if (!towers.includes(a.tower)) towers.push(a.tower);
                                    if (a.tower === 'Tower A' && a.floor && !floorsA.includes(a.floor)) floorsA.push(a.floor);
                                    if (a.tower === 'Tower B' && a.floor && !floorsB.includes(a.floor)) floorsB.push(a.floor);
                                  }
                                  if (a.assignment_type === 'security_post' && a.security_post && !secPosts.includes(a.security_post)) secPosts.push(a.security_post);
                                  if (a.assignment_type === 'cleaning_section' && a.cleaning_section && !cleanSections.includes(a.cleaning_section)) cleanSections.push(a.cleaning_section);
                                });
                                setAddStaffForm({
                                  name: member.name,
                                  phone: member.phone || '',
                                  staff_type: member.staff_type,
                                  work_start_time: member.work_start_time ? member.work_start_time.slice(0, 5) : '08:30',
                                  work_end_time: member.work_end_time ? member.work_end_time.slice(0, 5) : '16:15',
                                  working_days: member.working_days || [],
                                  towers,
                                  floorsA,
                                  floorsB,
                                  securityPosts: secPosts,
                                  cleaningSections: cleanSections,
                                });
                                setAllocationStatus(member.is_active ? 'present' : 'absent');
                                setAllocationDate(today);
                                setShowAllocationModal(true);
                              }}
                              className="flex items-center justify-center bg-transparent border-0 cursor-pointer transition-all"
                              style={{ width: '32px', height: '32px', borderRadius: '6px', color: '#043959' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(4,57,89,0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              title="Edit"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteStaff(member.id)}
                              className="flex items-center justify-center bg-transparent border-0 cursor-pointer transition-all"
                              style={{ width: '32px', height: '32px', borderRadius: '6px', color: '#EF4444' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              title="Delete"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        </>)}

        {/* ====== STAFF DIRECTORY TAB ====== */}
        {activeTab === 'staff' && (
          <div>
            <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Staff Directory</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAllocationModal(true)}
                  className="inline-flex items-center gap-2 text-sm font-medium rounded-lg transition-all hover:opacity-90 cursor-pointer"
                  style={{ padding: '10px 20px', background: 'white', color: '#043959', border: '1px solid #043959', fontFamily: "'Lexend', sans-serif" }}
                >
                  Staff Allocation
                </button>
                <button
                  onClick={() => setShowAddStaffModal(true)}
                  className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
                  style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                >
                  <span>+</span> Add Staff
                </button>
              </div>
            </div>

            {/* Search, Filter & Staff Select */}
            <div className="flex flex-wrap gap-3" style={{ marginBottom: '24px' }}>
              <input
                type="text"
                placeholder="Search by name..."
                value={directorySearch}
                onChange={(e) => setDirectorySearch(e.target.value)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', minWidth: '220px',
                }}
              />
              <select
                value={directoryType}
                onChange={(e) => setDirectoryType(e.target.value)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
                }}
              >
                <option value="">All Types</option>
                <option value="cleaning">Cleaning</option>
                <option value="security">Security</option>
              </select>
              <select
                value={expandedStaffId || ''}
                onChange={(e) => setExpandedStaffId(e.target.value ? Number(e.target.value) : null)}
                style={{
                  padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                  fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none', minWidth: '200px',
                }}
              >
                <option value="">Select Staff Member</option>
                {filteredDirectory.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading staff...</div>
            ) : filteredDirectory.length === 0 ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>No staff found</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {filteredDirectory.map((member) => {
                  const summary = getAssignmentSummary(member);
                  return (
                    <div key={member.id} className="bg-white" style={{ border: '1px solid #E5E5E0', borderRadius: '12px', padding: '20px' }}>
                      {/* Staff Header */}
                      <div className="flex items-center gap-4" style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #F0F0EB' }}>
                        {member.photo_path ? (
                          <img
                            src={`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace('/api', '')}/storage/${member.photo_path}`}
                            alt={member.name}
                            style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div
                            className="flex items-center justify-center text-white font-semibold"
                            style={{
                              width: '56px', height: '56px', borderRadius: '50%', fontSize: '20px', flexShrink: 0,
                              background: 'linear-gradient(135deg, #043959, #F4A460)',
                            }}
                          >
                            {getInitials(member.name)}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', color: '#1A1A1A' }}>{member.name}</h4>
                          <p style={{ fontSize: '13px', color: '#999999' }}>
                            {member.staff_type === 'cleaning' ? 'Cleaning Staff' : 'Security Staff'}
                          </p>
                        </div>
                        {member.aadhar_path && (
                          <div
                            title="View Aadhaar"
                            onClick={() => window.open(`${(process.env.REACT_APP_API_URL || 'http://tgcaoa-backend.test/api').replace('/api', '')}/storage/${member.aadhar_path}`, '_blank')}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                              width: '40px', height: '40px', borderRadius: '8px', flexShrink: 0,
                              background: '#F0F0EB', transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#E5E5E0'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#F0F0EB'; }}
                          >
                            <img src="/aadhaar-icon.svg" alt="Aadhaar" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                          </div>
                        )}
                      </div>

                      {/* Cleaning: Tower & Floor Assignments */}
                      {member.staff_type === 'cleaning' && summary?.towers && Object.keys(summary.towers).length > 0 && (
                        <>
                          <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Tower Assignment</div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                              {Object.keys(summary.towers).join(', ') || '-'}
                            </div>
                          </div>
                          <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Floor Assignment</div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                              {Object.entries(summary.towers).map(([, floors]) => `Floors ${floors.join(', ')}`).join(' | ') || '-'}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Cleaning: Sections */}
                      {member.staff_type === 'cleaning' && summary?.sections?.length > 0 && (
                        <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Cleaning Sections</div>
                          <div className="flex flex-wrap gap-1" style={{ marginTop: '4px' }}>
                            {summary.sections.map((section) => (
                              <span
                                key={section}
                                className="inline-flex items-center"
                                style={{
                                  padding: '4px 8px', borderRadius: '6px',
                                  fontSize: '11px', fontWeight: 600,
                                  background: '#043959', color: 'white',
                                }}
                              >
                                {section}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Security: Posts */}
                      {member.staff_type === 'security' && summary?.posts && (
                        <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Security Post</div>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                            {summary.posts.join(', ') || '-'}
                          </div>
                        </div>
                      )}

                      {/* Working Days */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Days</div>
                        <div className="flex flex-wrap gap-1" style={{ marginTop: '4px' }}>
                          {allDays.map((day) => {
                            const isActive = member.working_days?.includes(day);
                            return (
                              <span
                                key={day}
                                className="inline-flex items-center justify-center"
                                style={{
                                  minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                                  fontSize: '11px', fontWeight: 600,
                                  background: isActive ? '#043959' : 'white',
                                  color: isActive ? 'white' : '#999999',
                                  border: isActive ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                                }}
                              >
                                {dayLabels[day]}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Working Hours */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Working Hours</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {formatTime12(member.work_start_time)} - {formatTime12(member.work_end_time)}
                        </div>
                      </div>

                      {/* Contact */}
                      <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '0' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Contact</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                          {member.phone || '-'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2" style={{ marginTop: '16px' }}>
                        <button
                          onClick={() => {
                            resetAddStaffForm();
                            setEditingStaffId(member.id);
                            const towers = [];
                            const floorsA = [];
                            const floorsB = [];
                            const secPosts = [];
                            const cleanSections = [];
                            (member.assignments || []).forEach((a) => {
                              if (a.assignment_type === 'tower_floor' && a.tower) {
                                if (!towers.includes(a.tower)) towers.push(a.tower);
                                if (a.tower === 'Tower A' && a.floor && !floorsA.includes(a.floor)) floorsA.push(a.floor);
                                if (a.tower === 'Tower B' && a.floor && !floorsB.includes(a.floor)) floorsB.push(a.floor);
                              }
                              if (a.assignment_type === 'security_post' && a.security_post && !secPosts.includes(a.security_post)) secPosts.push(a.security_post);
                              if (a.assignment_type === 'cleaning_section' && a.cleaning_section && !cleanSections.includes(a.cleaning_section)) cleanSections.push(a.cleaning_section);
                            });
                            setAddStaffForm({
                              name: member.name,
                              phone: member.phone || '',
                              staff_type: member.staff_type,
                              work_start_time: member.work_start_time ? member.work_start_time.slice(0, 5) : '08:30',
                              work_end_time: member.work_end_time ? member.work_end_time.slice(0, 5) : '16:15',
                              working_days: member.working_days || [],
                              towers,
                              floorsA,
                              floorsB,
                              securityPosts: secPosts,
                              cleaningSections: cleanSections,
                            });
                            setAllocationStatus(member.is_active ? 'present' : 'absent');
                            setAllocationDate(today);
                            setShowAllocationModal(true);
                          }}
                          className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                          style={{
                            flex: 1, padding: '8px 12px',
                            background: '#FAFAF8', color: '#043959',
                            border: '1px solid #E5E5E0',
                            fontFamily: "'Lexend', sans-serif",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#043959';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#E5E5E0';
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(member.id)}
                          className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                          style={{
                            flex: 1, padding: '8px 12px',
                            background: '#FAFAF8', color: '#1A1A1A',
                            border: '1px solid #E5E5E0',
                            fontFamily: "'Lexend', sans-serif",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#EF4444';
                            e.currentTarget.style.color = '#EF4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#E5E5E0';
                            e.currentTarget.style.color = '#1A1A1A';
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ====== ATTENDANCE TRACKER TAB ====== */}
        {activeTab === 'attendance' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Attendance Tracker</h3>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                />
                <select
                  value={attendanceTypeFilter}
                  onChange={(e) => setAttendanceTypeFilter(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', background: 'white', color: '#1A1A1A',
                  }}
                >
                  <option value="">All Types</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="security">Security</option>
                </select>
                <select
                  value={attendanceStatusFilter}
                  onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 14px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', background: 'white', color: '#1A1A1A',
                  }}
                >
                  <option value="">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="leave">On Leave</option>
                </select>
                {(attendanceTypeFilter || attendanceStatusFilter) && (
                  <button
                    onClick={() => { setAttendanceTypeFilter(''); setAttendanceStatusFilter(''); }}
                    className="text-xs font-medium cursor-pointer border-0 bg-transparent"
                    style={{ color: '#EF4444', fontFamily: "'Lexend', sans-serif", padding: '8px 4px' }}
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={async () => {
                    try {
                      const entries = Object.entries(attendance).map(([id, status]) => ({
                        staff_member_id: Number(id),
                        status,
                      }));
                      await saveAttendance({ date: attendanceDate, attendance: entries });
                      alert('Attendance saved successfully!');
                    } catch (err) {
                      console.error('Failed to save attendance', err);
                      alert('Failed to save attendance');
                    }
                  }}
                  className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
                  style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                >
                  Save Attendance
                </button>
              </div>
            </div>

            {/* Summary Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Total', value: attendanceStats.total, bg: 'rgba(4,57,89,0.1)', color: '#043959' },
                { label: 'Present', value: attendanceStats.present, bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
                { label: 'Absent', value: attendanceStats.absent, bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
                { label: 'On Leave', value: attendanceStats.leave, bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
              ].map((s) => (
                <div key={s.label} className="bg-white text-center" style={{ padding: '16px', borderRadius: '10px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading staff...</div>
            ) : (
              <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Staff Name', 'Type', 'Mark Attendance'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allStaff
                        .filter((m) => !attendanceTypeFilter || m.staff_type === attendanceTypeFilter)
                        .filter((m) => !attendanceStatusFilter || (attendance[m.id] || (m.is_active ? 'present' : 'absent')) === attendanceStatusFilter)
                        .map((member) => {
                        const status = attendance[member.id] || 'absent';
                        const statusColors = {
                          present: { bg: 'rgba(16,185,129,0.1)', color: '#10B981', border: '#10B981' },
                          absent: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '#EF4444' },
                          leave: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '#F59E0B' },
                        };
                        return (
                          <tr key={member.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div className="flex items-center gap-3">
                                <div
                                  className="flex items-center justify-center text-white font-semibold"
                                  style={{
                                    width: '32px', height: '32px', borderRadius: '50%', fontSize: '11px',
                                    background: 'linear-gradient(135deg, #043959, #F4A460)', flexShrink: 0,
                                  }}
                                >
                                  {getInitials(member.name)}
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{member.name}</span>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{
                                fontSize: '12px', fontWeight: 500, padding: '3px 8px', borderRadius: '4px',
                                background: member.staff_type === 'security' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)',
                                color: member.staff_type === 'security' ? '#3B82F6' : '#10B981',
                              }}>
                                {member.staff_type === 'security' ? 'Security' : 'Cleaning'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div className="flex gap-2">
                                {['present', 'absent', 'leave'].map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => setAttendance((prev) => ({ ...prev, [member.id]: s }))}
                                    className="text-xs font-medium rounded-md cursor-pointer transition-all"
                                    style={{
                                      padding: '5px 12px',
                                      fontFamily: "'Lexend', sans-serif",
                                      background: status === s ? statusColors[s].bg : 'white',
                                      color: status === s ? statusColors[s].color : '#666666',
                                      border: `1px solid ${status === s ? statusColors[s].border : '#E5E5E0'}`,
                                    }}
                                  >
                                    {s === 'present' ? 'Present' : s === 'absent' ? 'Absent' : 'Leave'}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== LEAVE MANAGEMENT TAB ====== */}
        {activeTab === 'leave' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Leave Management</h3>
              <button
                onClick={() => setShowLeaveModal(true)}
                className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
              >
                + Apply Leave
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Total Requests', value: leaveStats.total, bg: 'rgba(4,57,89,0.1)', color: '#043959' },
                { label: 'Pending', value: leaveStats.pending, bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
                { label: 'Approved', value: leaveStats.approved, bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
                { label: 'Rejected', value: leaveStats.rejected, bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
              ].map((s) => (
                <div key={s.label} className="bg-white text-center" style={{ padding: '16px', borderRadius: '10px', border: '1px solid #E5E5E0' }}>
                  <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Leave Requests Table */}
            {leaveRequests.length === 0 ? (
              <div className="bg-white text-center text-sm" style={{ padding: '60px 16px', color: '#999999', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                No leave requests yet. Click "Apply Leave" to create one.
              </div>
            ) : (
              <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                        {['Staff Name', 'Leave Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Actions'].map((h) => (
                          <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leaveRequests.map((req) => (
                        <tr key={req.id} style={{ borderBottom: '1px solid #F0F0EB' }}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{req.staffName}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{req.leaveType}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666' }}>{new Date(req.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666' }}>{new Date(req.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{req.days}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#666666', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.reason || '-'}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                              background: req.status === 'Approved' ? 'rgba(16,185,129,0.1)' : req.status === 'Rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                              color: req.status === 'Approved' ? '#10B981' : req.status === 'Rejected' ? '#EF4444' : '#F59E0B',
                            }}>
                              {req.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {req.status === 'Pending' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => updateLeaveStatus(req.id, 'Approved')}
                                  className="text-xs font-medium rounded-md cursor-pointer"
                                  style={{ padding: '4px 10px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid #10B981', fontFamily: "'Lexend', sans-serif" }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => updateLeaveStatus(req.id, 'Rejected')}
                                  className="text-xs font-medium rounded-md cursor-pointer"
                                  style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid #EF4444', fontFamily: "'Lexend', sans-serif" }}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Leave Modal */}
            {showLeaveModal && (
              <div
                className="fixed inset-0 flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1000 }}
                onClick={(e) => { if (e.target === e.currentTarget) setShowLeaveModal(false); }}
              >
                <div className="bg-white" style={{ borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', margin: '0 16px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', marginBottom: '20px' }}>Apply for Leave</h4>
                  <div className="flex flex-col" style={{ gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Staff Member</label>
                      <select
                        value={leaveForm.staffId}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, staffId: e.target.value }))}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                      >
                        <option value="">Select staff...</option>
                        {allStaff.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Leave Type</label>
                      <select
                        value={leaveForm.leaveType}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveType: e.target.value }))}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                      >
                        <option value="Casual">Casual Leave</option>
                        <option value="Sick">Sick Leave</option>
                        <option value="Earned">Earned Leave</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>From Date</label>
                        <input
                          type="date"
                          value={leaveForm.fromDate}
                          onChange={(e) => setLeaveForm((prev) => ({ ...prev, fromDate: e.target.value }))}
                          className="w-full"
                          style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>To Date</label>
                        <input
                          type="date"
                          value={leaveForm.toDate}
                          onChange={(e) => setLeaveForm((prev) => ({ ...prev, toDate: e.target.value }))}
                          className="w-full"
                          style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, color: '#666666', display: 'block', marginBottom: '4px' }}>Reason</label>
                      <textarea
                        value={leaveForm.reason}
                        onChange={(e) => setLeaveForm((prev) => ({ ...prev, reason: e.target.value }))}
                        placeholder="Optional reason..."
                        rows={3}
                        className="w-full"
                        style={{ padding: '8px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2" style={{ marginTop: '20px' }}>
                    <button
                      onClick={() => setShowLeaveModal(false)}
                      className="text-sm font-medium rounded-lg cursor-pointer"
                      style={{ padding: '8px 16px', background: '#FAFAF8', color: '#666666', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLeaveSubmit}
                      className="text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                      style={{ padding: '8px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== REPORTS TAB ====== */}
        {activeTab === 'reports' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Reports & Overview</h3>
            </div>

            {allStaffLoading ? (
              <div className="text-center text-sm" style={{ padding: '60px 16px', color: '#999999' }}>Loading data...</div>
            ) : (
              <>
                {/* Staff by Type */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Staff Count by Type</div>
                  <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: '12px' }}>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#10B981' }}>{reportData.cleaningCount}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Cleaning Staff</div>
                    </div>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#3B82F6' }}>{reportData.secCount}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Security Staff</div>
                    </div>
                    <div className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="text-3xl font-bold" style={{ color: '#043959' }}>{allStaff.length}</div>
                      <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>Total Staff</div>
                    </div>
                  </div>
                </div>

                {/* Shift Distribution */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Shift Distribution</div>
                  <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: '12px' }}>
                    {[
                      { label: 'Day Shift', value: reportData.shifts.day, color: '#F59E0B' },
                      { label: 'Evening Shift', value: reportData.shifts.evening, color: '#F4A460' },
                      { label: 'Night Shift', value: reportData.shifts.night, color: '#6366F1' },
                    ].map((s) => (
                      <div key={s.label} className="bg-white" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                        <div className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</div>
                        <div className="text-xs font-medium" style={{ color: '#666666', marginTop: '4px' }}>{s.label}</div>
                        <div style={{ marginTop: '8px', background: '#F0F0EB', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                          <div style={{ width: allStaff.length > 0 ? `${(s.value / allStaff.length) * 100}%` : '0%', height: '100%', background: s.color, borderRadius: '4px' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tower/Post Allocation */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Staff Allocation by Area</div>
                  {Object.keys(reportData.towerAllocation).length === 0 ? (
                    <div className="bg-white text-center text-sm" style={{ padding: '32px', color: '#999999', borderRadius: '12px', border: '1px solid #E5E5E0' }}>No allocation data</div>
                  ) : (
                    <div className="bg-white overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div className="overflow-x-auto">
                        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Area / Post</th>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>Staff Assigned</th>
                              <th className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666', width: '40%' }}>Distribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(reportData.towerAllocation).sort((a, b) => b[1] - a[1]).map(([area, count]) => {
                              const maxCount = Math.max(...Object.values(reportData.towerAllocation));
                              return (
                                <tr key={area} style={{ borderBottom: '1px solid #F0F0EB' }}>
                                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>{area}</td>
                                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1A1A1A' }}>{count}</td>
                                  <td style={{ padding: '12px 16px' }}>
                                    <div style={{ background: '#F0F0EB', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                      <div style={{ width: `${(count / maxCount) * 100}%`, height: '100%', background: '#043959', borderRadius: '4px' }} />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Working Days Coverage */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Working Days Coverage</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7" style={{ gap: '8px' }}>
                    {Object.entries(reportData.dayCoverage).map(([day, count]) => {
                      const maxDay = Math.max(...Object.values(reportData.dayCoverage), 1);
                      const intensity = count / maxDay;
                      return (
                        <div
                          key={day}
                          className="bg-white text-center"
                          style={{
                            padding: '14px 8px', borderRadius: '10px',
                            border: `1px solid ${intensity < 0.5 && count > 0 ? '#F59E0B' : '#E5E5E0'}`,
                          }}
                        >
                          <div className="text-lg font-bold" style={{ color: '#1A1A1A' }}>{count}</div>
                          <div style={{ fontSize: '11px', color: '#666666', marginTop: '2px' }}>{day.slice(0, 3)}</div>
                          <div style={{ marginTop: '6px', background: '#F0F0EB', borderRadius: '3px', height: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${intensity * 100}%`, height: '100%', background: intensity < 0.5 ? '#F59E0B' : '#10B981', borderRadius: '3px' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      <Modal isOpen={showAddStaffModal} onClose={() => setShowAddStaffModal(false)} title="Add Staff" size="lg">
        <form onSubmit={handleAddStaffSubmit} className="space-y-4">
          <FormInput label="Staff Name *" name="name" value={addStaffForm.name} onChange={handleAddStaffChange} placeholder="Enter staff name" required />

          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Staff Type *</label>
            <select
              name="staff_type"
              value={addStaffForm.staff_type}
              onChange={(e) => setAddStaffForm({ ...addStaffForm, staff_type: e.target.value })}
              className="w-full cursor-pointer"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
              }}
            >
              <option value="">Select Type</option>
              <option value="cleaning">Cleaning Staff</option>
              <option value="security">Security Staff</option>
            </select>
          </div>

          {/* Working Hours */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Hours *</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>Start Time</label>
                <input
                  type="time"
                  name="work_start_time"
                  value={addStaffForm.work_start_time}
                  onChange={handleAddStaffChange}
                  className="w-full"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>End Time</label>
                <input
                  type="time"
                  name="work_end_time"
                  value={addStaffForm.work_end_time}
                  onChange={handleAddStaffChange}
                  className="w-full"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                  }}
                  required
                />
              </div>
            </div>
          </div>

          {/* Working Days */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Days *</label>
            <div className="flex flex-wrap gap-2" style={{ marginTop: '8px' }}>
              {allDays.map((day) => (
                <div
                  key={day}
                  onClick={() => toggleAddStaffDay(day)}
                  className="cursor-pointer inline-flex items-center justify-center"
                  style={{
                    minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                    fontSize: '11px', fontWeight: 600, userSelect: 'none',
                    background: addStaffForm.working_days.includes(day) ? '#043959' : 'white',
                    color: addStaffForm.working_days.includes(day) ? 'white' : '#999999',
                    border: addStaffForm.working_days.includes(day) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                    transition: 'all 0.2s',
                  }}
                >
                  {dayLabels[day]}
                </div>
              ))}
            </div>
          </div>

          {/* Photo & Aadhar Upload */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Profile Photo</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
                <span style={{ fontSize: '13px', color: photoFile ? '#1A1A1A' : '#999999' }}>
                  {photoFile ? photoFile.name : 'Choose photo'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPhotoFile(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Aadhar Document</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ fontSize: '13px', color: aadharFile ? '#1A1A1A' : '#999999' }}>
                  {aadharFile ? aadharFile.name : 'Choose file (image/PDF)'}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setAadharFile(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => setShowAddStaffModal(false)}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{
                padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A',
                border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addStaffSubmitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{
                padding: '10px 20px', background: '#043959',
                fontFamily: "'Lexend', sans-serif", opacity: addStaffSubmitting ? 0.7 : 1,
              }}
            >
              {addStaffSubmitting ? 'Adding...' : 'Add Staff'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Staff Allocation Modal (full form with assignments) */}
      <Modal isOpen={showAllocationModal} onClose={() => setShowAllocationModal(false)} title="Staff Allocation" size="3xl">
        <form onSubmit={handleAddStaffSubmit} className="space-y-4">
          {/* Staff Type */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Staff Type *</label>
            <select
              name="staff_type"
              value={addStaffForm.staff_type}
              onChange={(e) => setAddStaffForm({ ...addStaffForm, staff_type: e.target.value, name: '', towers: [], floorsA: [], floorsB: [], securityPosts: [], cleaningSections: [] })}
              className="w-full cursor-pointer"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
              }}
            >
              <option value="">Select Type</option>
              <option value="cleaning">Cleaning Staff</option>
              <option value="security">Security Staff</option>
            </select>
          </div>

          {/* Staff Name Dropdown */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Staff Name *</label>
            <select
              name="name"
              value={addStaffForm.name}
              onChange={(e) => {
                const selectedName = e.target.value;
                const combined = [...allStaff, ...staff];
                const seen = new Set();
                const uniqueStaff = combined.filter((s) => {
                  if (seen.has(s.id)) return false;
                  seen.add(s.id);
                  return true;
                });
                const member = uniqueStaff.find((s) => s.name === selectedName);
                if (member) {
                  setEditingStaffId(member.id);
                  const towers = [];
                  const floorsA = [];
                  const floorsB = [];
                  const secPosts = [];
                  const cleanSections = [];
                  (member.assignments || []).forEach((a) => {
                    if (a.assignment_type === 'tower_floor' && a.tower) {
                      if (!towers.includes(a.tower)) towers.push(a.tower);
                      if (a.tower === 'Tower A' && a.floor && !floorsA.includes(a.floor)) floorsA.push(a.floor);
                      if (a.tower === 'Tower B' && a.floor && !floorsB.includes(a.floor)) floorsB.push(a.floor);
                    }
                    if (a.assignment_type === 'security_post' && a.security_post && !secPosts.includes(a.security_post)) secPosts.push(a.security_post);
                    if (a.assignment_type === 'cleaning_section' && a.cleaning_section && !cleanSections.includes(a.cleaning_section)) cleanSections.push(a.cleaning_section);
                  });
                  setAddStaffForm({
                    ...addStaffForm,
                    name: selectedName,
                    phone: member.phone || '',
                    work_start_time: member.work_start_time ? member.work_start_time.slice(0, 5) : '08:30',
                    work_end_time: member.work_end_time ? member.work_end_time.slice(0, 5) : '16:15',
                    working_days: member.working_days || [],
                    towers,
                    floorsA,
                    floorsB,
                    securityPosts: secPosts,
                    cleaningSections: cleanSections,
                  });
                } else {
                  setEditingStaffId(null);
                  setAddStaffForm({ ...addStaffForm, name: selectedName });
                }
              }}
              className="w-full cursor-pointer"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
              }}
              required
            >
              <option value="">Select Staff</option>
              {(() => {
                const combined = [...allStaff, ...staff];
                const seen = new Set();
                return combined
                  .filter((s) => {
                    if (seen.has(s.name)) return false;
                    seen.add(s.name);
                    return !addStaffForm.staff_type || s.staff_type === addStaffForm.staff_type;
                  })
                  .map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ));
              })()}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Date *</label>
            <input
              type="date"
              value={allocationDate}
              onChange={(e) => setAllocationDate(e.target.value)}
              className="w-full"
              style={{
                padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
              }}
              required
            />
          </div>

          {/* Present / Absent Toggle */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Status *</label>
            <div className="flex gap-2">
              {['present', 'absent'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setAllocationStatus(s)}
                  className="text-sm font-medium rounded-lg cursor-pointer transition-all"
                  style={{
                    padding: '8px 24px',
                    fontFamily: "'Lexend', sans-serif",
                    background: allocationStatus === s
                      ? (s === 'present' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)')
                      : 'white',
                    color: allocationStatus === s
                      ? (s === 'present' ? '#10B981' : '#EF4444')
                      : '#666666',
                    border: `1.5px solid ${allocationStatus === s
                      ? (s === 'present' ? '#10B981' : '#EF4444')
                      : '#E5E5E0'}`,
                  }}
                >
                  {s === 'present' ? 'Present' : 'Absent'}
                </button>
              ))}
            </div>
          </div>

          {/* Cleaning Assignment - only when present */}
          {allocationStatus === 'present' && addStaffForm.staff_type === 'cleaning' && (
            <>
              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Area *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '8px' }}>
                  {['Tower A', 'Tower B', 'Parking', 'Others'].map((tower) => (
                    <label
                      key={tower}
                      className="flex items-center gap-2 cursor-pointer"
                      style={{
                        padding: '10px 12px', borderRadius: '8px',
                        border: addStaffForm.towers.includes(tower) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                        background: addStaffForm.towers.includes(tower) ? 'rgba(4,57,89,0.05)' : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addStaffForm.towers.includes(tower)}
                        onChange={() => toggleAddStaffArrayItem('towers', tower)}
                        style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>
                        {tower === 'Parking' ? 'Parking Floors' : tower}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '4px' }}>
                  {addStaffForm.towers.length === 1 && addStaffForm.towers.includes('Others') ? 'Cleaning Sections' : 'Floor Assignment *'}
                </label>
                <p style={{ fontSize: '12px', color: '#999999', marginBottom: '8px' }}>
                  {addStaffForm.towers.length === 1 && addStaffForm.towers.includes('Others') ? 'Select the cleaning task areas for this staff member' : 'Select individual floors for this staff member'}
                </p>

                {addStaffForm.towers.includes('Tower A') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Tower A Floors</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                      {towerAFloors.map((floor) => (
                        <label
                          key={`a-${floor}`}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: addStaffForm.floorsA.includes(floor) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: addStaffForm.floorsA.includes(floor) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={addStaffForm.floorsA.includes(floor)}
                            onChange={() => toggleAddStaffArrayItem('floorsA', floor)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>Floor {floor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {addStaffForm.towers.includes('Tower B') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Tower B Floors</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                      {towerBFloors.map((floor) => (
                        <label
                          key={`b-${floor}`}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: addStaffForm.floorsB.includes(floor) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: addStaffForm.floorsB.includes(floor) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={addStaffForm.floorsB.includes(floor)}
                            onChange={() => toggleAddStaffArrayItem('floorsB', floor)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>Floor {floor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {addStaffForm.towers.includes('Others') && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Others</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                      {otherCleaningSections.map((section) => (
                        <label
                          key={section}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: addStaffForm.cleaningSections.includes(section) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: addStaffForm.cleaningSections.includes(section) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={addStaffForm.cleaningSections.includes(section)}
                            onChange={() => toggleAddStaffArrayItem('cleaningSections', section)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{section}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {(addStaffForm.towers.includes('Tower A') || addStaffForm.towers.includes('Tower B') || addStaffForm.towers.includes('Parking')) && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#043959' }}>Cleaning Sections</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '12px' }}>
                      {(() => {
                        const hasParking = addStaffForm.towers.includes('Parking');
                        const hasTower = addStaffForm.towers.includes('Tower A') || addStaffForm.towers.includes('Tower B');
                        if (hasParking && !hasTower) return parkingCleaningSections;
                        if (!hasParking && hasTower) return towerCleaningSections;
                        if (hasParking && hasTower) return [...towerCleaningSections, ...parkingCleaningSections];
                        return [];
                      })().map((section) => (
                        <label
                          key={section}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '10px 12px', borderRadius: '8px',
                            border: addStaffForm.cleaningSections.includes(section) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                            background: addStaffForm.cleaningSections.includes(section) ? 'rgba(4,57,89,0.05)' : 'white',
                            transition: 'all 0.2s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={addStaffForm.cleaningSections.includes(section)}
                            onChange={() => toggleAddStaffArrayItem('cleaningSections', section)}
                            style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{section}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Security Assignment - only when present */}
          {allocationStatus === 'present' && addStaffForm.staff_type === 'security' && (
            <>
              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '4px' }}>Security Post Assignment *</label>
                <p style={{ fontSize: '12px', color: '#999999', marginBottom: '8px' }}>Select the security post(s) for this staff member</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  {securityPostsList.map((post) => (
                    <label
                      key={post}
                      className="flex items-center gap-2 cursor-pointer"
                      style={{
                        padding: '10px 12px', borderRadius: '8px',
                        border: addStaffForm.securityPosts.includes(post) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                        background: addStaffForm.securityPosts.includes(post) ? 'rgba(4,57,89,0.05)' : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addStaffForm.securityPosts.includes(post)}
                        onChange={() => toggleAddStaffArrayItem('securityPosts', post)}
                        style={{ width: '18px', height: '18px', accentColor: '#043959', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, userSelect: 'none' }}>{post}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Shift Type</label>
                <select
                  value={`${addStaffForm.work_start_time}-${addStaffForm.work_end_time}`}
                  onChange={(e) => {
                    const [start, end] = e.target.value.split('-');
                    setAddStaffForm({ ...addStaffForm, work_start_time: start, work_end_time: end });
                  }}
                  className="w-full cursor-pointer"
                  style={{
                    padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none',
                  }}
                >
                  <option value="">Select Shift</option>
                  <option value="08:00-16:00">Day Shift (8:00 AM - 4:00 PM)</option>
                  <option value="16:00-00:00">Evening Shift (4:00 PM - 12:00 AM)</option>
                  <option value="00:00-08:00">Night Shift (12:00 AM - 8:00 AM)</option>
                </select>
              </div>
            </>
          )}

          {/* Working Days - only when present */}
          {allocationStatus === 'present' && (
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Days *</label>
              <div className="flex flex-wrap gap-2" style={{ marginTop: '8px' }}>
                {allDays.map((day) => (
                  <div
                    key={day}
                    onClick={() => toggleAddStaffDay(day)}
                    className="cursor-pointer inline-flex items-center justify-center"
                    style={{
                      minWidth: '36px', padding: '4px 8px', borderRadius: '6px',
                      fontSize: '11px', fontWeight: 600, userSelect: 'none',
                      background: addStaffForm.working_days.includes(day) ? '#043959' : 'white',
                      color: addStaffForm.working_days.includes(day) ? 'white' : '#999999',
                      border: addStaffForm.working_days.includes(day) ? '1.5px solid #043959' : '1.5px solid #E5E5E0',
                      transition: 'all 0.2s',
                    }}
                  >
                    {dayLabels[day]}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Working Hours (for cleaning) - only when present */}
          {allocationStatus === 'present' && addStaffForm.staff_type === 'cleaning' && (
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Working Hours *</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>Start Time</label>
                  <input
                    type="time"
                    name="work_start_time"
                    value={addStaffForm.work_start_time}
                    onChange={handleAddStaffChange}
                    className="w-full"
                    style={{
                      padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                      fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#999999', marginBottom: '4px', display: 'block' }}>End Time</label>
                  <input
                    type="time"
                    name="work_end_time"
                    value={addStaffForm.work_end_time}
                    onChange={handleAddStaffChange}
                    className="w-full"
                    style={{
                      padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px',
                      fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none',
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Photo & Aadhar Upload */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Profile Photo</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
                <span style={{ fontSize: '13px', color: photoFile ? '#1A1A1A' : '#999999' }}>
                  {photoFile ? photoFile.name : 'Choose photo'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPhotoFile(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Aadhar Document</label>
              <label
                className="flex items-center gap-2 cursor-pointer"
                style={{
                  padding: '10px 12px', border: '1px dashed #E5E5E0', borderRadius: '8px',
                  background: '#FAFAF8', transition: 'all 0.2s',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ fontSize: '13px', color: aadharFile ? '#1A1A1A' : '#999999' }}>
                  {aadharFile ? aadharFile.name : 'Choose file (image/PDF)'}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setAadharFile(file);
                    }
                  }}
                />
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => setShowAllocationModal(false)}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{
                padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A',
                border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addStaffSubmitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{
                padding: '10px 20px', background: '#043959',
                fontFamily: "'Lexend', sans-serif", opacity: addStaffSubmitting ? 0.7 : 1,
              }}
            >
              {addStaffSubmitting ? 'Saving...' : 'Save Allocation'}
            </button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
